const SubstanceDeclarationEvaluationStatus = Object.freeze({
    RESOLVED: 'resolved',
    AMBIGUOUS: 'ambiguous',
    NOT_APPLICABLE: 'not_applicable',
})

const SubstanceDeclarationOutcome = Object.freeze({
    ABOVE: 'above',
    BELOW: 'below',
    AMBIGUOUS: 'ambiguous',
})

const SubstanceDeclarationClaim = Object.freeze({
    ABOVE: 'substances_above_threshold',
    BELOW: 'substances_below_threshold',
})

const PERCENT_WW_UNITS = Object.freeze([
    '%',
    '%w/w',
    'percent',
    'percentw/w',
    'w/w',
])

const normalizeToken = value => (
    typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '') : ''
)

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key)

const isPercentWeightByWeight = concentration => {
    const unit = concentration && hasOwn(concentration, 'unit')
        ? concentration.unit
        : null
    const basis = concentration && hasOwn(concentration, 'basis')
        ? concentration.basis
        : null
    const normalizedUnit = normalizeToken(unit)
    const normalizedBasis = normalizeToken(basis)
    const supportedUnit = !normalizedUnit || PERCENT_WW_UNITS.includes(normalizedUnit)
    const supportedBasis = !normalizedBasis || normalizedBasis === 'w/w'

    return supportedUnit && supportedBasis
}

const claimTypes = claims => new Set(
    (Array.isArray(claims) ? claims : [])
        .filter(claim => claim && typeof claim.type === 'string')
        .map(claim => claim.type),
)

const emptySubstancesFallback = ({ policy, substances, claims }) => {
    const rows = Array.isArray(substances) ? substances : []
    const fallback = policy && policy.emptySubstancesFallback

    if (rows.length
        || !fallback
        || !claimTypes(claims).has(fallback.sourceClaim)) {
        return null
    }

    return {
        status: SubstanceDeclarationEvaluationStatus.RESOLVED,
        claimType: fallback.claimType,
        reason: 'empty-substances-claim-fallback',
        facts: [],
    }
}

const boundaryOutcomeFromClaims = claims => {
    const types = claimTypes(claims)
    const compliant = types.has('compliant')
    const nonCompliant = types.has('non_compliant')

    if (compliant === nonCompliant) {
        return SubstanceDeclarationOutcome.AMBIGUOUS
    }

    return compliant
        ? SubstanceDeclarationOutcome.BELOW
        : SubstanceDeclarationOutcome.ABOVE
}

const exactValueOutcome = (value, threshold, claims, boundaryOutcome) => {
    if (value < threshold) {
        return SubstanceDeclarationOutcome.BELOW
    }
    if (value > threshold) {
        return SubstanceDeclarationOutcome.ABOVE
    }

    if (boundaryOutcome === SubstanceDeclarationOutcome.BELOW
        || boundaryOutcome === SubstanceDeclarationOutcome.ABOVE) {
        return boundaryOutcome
    }

    return boundaryOutcome === 'claim-dependent'
        ? boundaryOutcomeFromClaims(claims)
        : SubstanceDeclarationOutcome.AMBIGUOUS
}

const concentrationOutcome = ({
    concentration,
    threshold,
    claims,
    missingValueOutcome,
    boundaryOutcome,
    crossingThresholdOutcome = SubstanceDeclarationOutcome.AMBIGUOUS,
}) => {
    if (!concentration
        || concentration.value === null
        || typeof concentration.value === 'undefined'
        || concentration.value === '') {
        return {
            outcome: missingValueOutcome === SubstanceDeclarationOutcome.BELOW
                ? SubstanceDeclarationOutcome.BELOW
                : SubstanceDeclarationOutcome.AMBIGUOUS,
            reason: 'missing-concentration',
        }
    }

    const value = Number(concentration.value)
    if (!Number.isFinite(value)) {
        return { outcome: SubstanceDeclarationOutcome.AMBIGUOUS, reason: 'invalid-value' }
    }
    if (!isPercentWeightByWeight(concentration)) {
        return { outcome: SubstanceDeclarationOutcome.AMBIGUOUS, reason: 'incompatible-unit' }
    }

    const operator = typeof concentration.operator === 'string'
        ? concentration.operator.trim()
        : ''
    if (!operator || operator === '=') {
        return {
            outcome: exactValueOutcome(value, threshold, claims, boundaryOutcome),
            reason: value === threshold ? 'threshold-boundary' : 'exact-value',
        }
    }
    if (operator === '<' || operator === '<=') {
        return value <= threshold
            ? { outcome: SubstanceDeclarationOutcome.BELOW, reason: 'upper-bound-below-threshold' }
            : {
                outcome: crossingThresholdOutcome,
                reason: crossingThresholdOutcome === SubstanceDeclarationOutcome.ABOVE
                    ? 'upper-bound-allows-threshold-exceedance'
                    : 'upper-bound-crosses-threshold',
            }
    }
    if (operator === '>' || operator === '>=') {
        return value >= threshold
            ? { outcome: SubstanceDeclarationOutcome.ABOVE, reason: 'lower-bound-above-threshold' }
            : {
                outcome: crossingThresholdOutcome,
                reason: crossingThresholdOutcome === SubstanceDeclarationOutcome.ABOVE
                    ? 'lower-bound-allows-threshold-exceedance'
                    : 'lower-bound-crosses-threshold',
            }
    }

    return { outcome: SubstanceDeclarationOutcome.AMBIGUOUS, reason: 'unsupported-operator' }
}

const manualStrategy = () => ({
    status: SubstanceDeclarationEvaluationStatus.NOT_APPLICABLE,
    claimType: null,
    reason: 'manual-strategy',
    facts: [],
})

const presenceStrategy = ({ substances }) => {
    const rows = Array.isArray(substances) ? substances : []
    if (!rows.length) {
        return {
            status: SubstanceDeclarationEvaluationStatus.NOT_APPLICABLE,
            claimType: null,
            reason: 'no-substances',
            facts: [],
        }
    }

    return {
        status: SubstanceDeclarationEvaluationStatus.RESOLVED,
        claimType: SubstanceDeclarationClaim.ABOVE,
        reason: 'substance-present',
        facts: rows.map((substance, index) => ({ index, outcome: 'present', substance })),
    }
}

const concentrationThresholdStrategy = ({
    policy,
    substances,
    claims,
    crossingThresholdOutcome = SubstanceDeclarationOutcome.AMBIGUOUS,
}) => {
    const rows = Array.isArray(substances) ? substances : []
    if (!rows.length) {
        return {
            status: SubstanceDeclarationEvaluationStatus.NOT_APPLICABLE,
            claimType: null,
            reason: 'no-substances',
            facts: [],
        }
    }

    const threshold = Number(policy.threshold.value)
    if (!Number.isFinite(threshold)
        || policy.aggregation !== 'any-above-otherwise-below') {
        return {
            status: SubstanceDeclarationEvaluationStatus.AMBIGUOUS,
            claimType: null,
            reason: 'invalid-threshold',
            facts: [],
        }
    }

    const facts = rows.map((substance, index) => ({
        index,
        ...concentrationOutcome({
            concentration: substance && substance.concentration,
            threshold,
            claims,
            missingValueOutcome: policy.missingValueOutcome,
            boundaryOutcome: policy.boundaryOutcome,
            crossingThresholdOutcome,
        }),
    }))
    if (facts.some(fact => fact.outcome === SubstanceDeclarationOutcome.ABOVE)) {
        return {
            status: SubstanceDeclarationEvaluationStatus.RESOLVED,
            claimType: SubstanceDeclarationClaim.ABOVE,
            reason: 'at-least-one-above',
            facts,
        }
    }
    if (facts.every(fact => fact.outcome === SubstanceDeclarationOutcome.BELOW)) {
        return {
            status: SubstanceDeclarationEvaluationStatus.RESOLVED,
            claimType: SubstanceDeclarationClaim.BELOW,
            reason: 'all-below',
            facts,
        }
    }

    return {
        status: SubstanceDeclarationEvaluationStatus.AMBIGUOUS,
        claimType: null,
        reason: 'ambiguous-substance-concentration',
        facts,
    }
}

const potentialExceedanceThresholdStrategy = input => concentrationThresholdStrategy({
    ...input,
    crossingThresholdOutcome: SubstanceDeclarationOutcome.ABOVE,
})

const BUILT_IN_STRATEGIES = Object.freeze({
    'manual-v1': manualStrategy,
    'concentration-threshold-v1': concentrationThresholdStrategy,
    'potential-exceedance-threshold-v1': potentialExceedanceThresholdStrategy,
    'presence-v1': presenceStrategy,
})

const createSubstanceDeclarationStrategyRegistry = ({ strategies = {} } = {}) => {
    const registered = Object.freeze({ ...BUILT_IN_STRATEGIES, ...strategies })

    return Object.freeze({
        evaluate(input = {}) {
            const policy = input.policy || { strategy: 'manual-v1' }
            const strategy = typeof registered[policy.strategy] === 'function'
                ? registered[policy.strategy]
                : manualStrategy
            const fallback = emptySubstancesFallback({ ...input, policy })

            return {
                strategy: typeof registered[policy.strategy] === 'function'
                    ? policy.strategy
                    : 'manual-v1',
                ...(fallback || strategy({ ...input, policy })),
            }
        },
    })
}

const substanceDeclarationStrategyRegistry = createSubstanceDeclarationStrategyRegistry()

export {
    SubstanceDeclarationClaim,
    SubstanceDeclarationEvaluationStatus,
    SubstanceDeclarationOutcome,
    createSubstanceDeclarationStrategyRegistry,
    substanceDeclarationStrategyRegistry,
}

export default substanceDeclarationStrategyRegistry
